import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import { hideSpinner, showSpinner } from "./spinner";
import isEmpty from "lodash/isEmpty";

const baseUrl = window.env?.REACT_APP_API_URL ?? "http://localhost:8080";
const loginCookie = window.env?.REACT_APP_LOGIN_COOKIE ?? "sl2sta_dev";
const token = document.cookie.match(`(^|;)\\s*${loginCookie}\\s*=\\s*([^;]+)`)?.pop() ?? "";

const rawBaseQuery = fetchBaseQuery({
  baseUrl,
  prepareHeaders: (headers) => {
    headers.set("authorization", `Bearer ${token}`);
    return headers;
  },
});

const baseQueryWithRequestId = async (args, api, extraOptions) => {
  const result = await rawBaseQuery(args, api, extraOptions);
  const requestId = result.meta?.response?.headers.get("x-request-id");

  return {
    ...result,
    meta: {
      ...result.meta,
      requestId,
    },
    ...(result.error && {
      error: {
        ...result.error,
        requestId,
      },
    }),
  };
};

export const api = createApi({
  reducerPath: "api",
  baseQuery: baseQueryWithRequestId,
  tagTypes: [
    "PbMarkets",
    "Quote",
    "QuoteSearch",
    "QuoteFloors",
    "QuoteFloor",
    "PdfOptions",
    "QuoteFloorPlanLaborCodes",
    "QuoteFloorProducts",
    "CoverLetter",
    "LaborCodes",
    "FavoriteProducts",
    "PrcPrices",
    "StoreLaborPrices",
  ],
  endpoints: (build) => ({
    getStoreInfo: build.query({ query: (body) => ({ method: "POST", url: `/fc-site-repo/search/storeInfo`, body }) }),
    getStates: build.query({ query: () => `/fc-site-repo/states` }), // TODO: replace with hardcoded list of states
    getDepartments: build.query({ query: () => `/fc-site-repo/departments` }),
    getEmployee: build.query({
      // TODO: replace with GET /fc-price-quote/employee/{ID} once that endpoint is in production
      query: (userId) => ({ method: "POST", url: `/fc-price-quote/get/employee/details/${userId}` }),
    }),
    getCanUserUpdatePrc: build.query({
      query: (body) => ({ method: `POST`, url: `/fc-price-quote/canUserUpdatePrc`, body }),
    }),
    getPbTypes: build.query({ query: () => `/pb/types` }),
    getPbMarkets: build.query({
      query: (programId) => `/pb/program/${programId}/markets`,
      providesTags: ["PbMarkets"],
    }),
    addPbMarket: build.mutation({
      query: (body) => ({ method: `POST`, url: `/pb/market`, body }),
      invalidatesTags: ["PbMarkets"],
    }),
    updatePbMarket: build.mutation({
      query: (body) => ({ method: `PATCH`, url: `/pb/market`, body }),
      invalidatesTags: ["PbMarkets"],
    }),
    deletePbMarket: build.mutation({
      query: (marketId) => ({ method: `DELETE`, url: `/pb/market/${marketId}` }),
      invalidatesTags: ["PbMarkets"],
    }),
    getPriceQuote: build.query({
      query: (quoteId) => `/quotes/${quoteId}`,
      providesTags: ["Quote"],
    }),
    updatePriceQuote: build.mutation({
      query: ({ quoteId, ...body }) => ({ method: `PATCH`, url: `/quotes/${quoteId}`, body }),
      invalidatesTags: ["Quote", "PrcPrices"],
      onQueryStarted: ({ quoteId, ...body }, { dispatch }) => {
        dispatch(api.util.updateQueryData("getPriceQuote", quoteId, (res) => Object.assign(res, body)));
      },
    }),
    getPriceQuoteFloors: build.query({
      query: (quoteId) => `/quotes/${quoteId}/floors`,
      transformResponse: (res) => res?._embedded?.quoteFloors ?? [],
      providesTags: ["QuoteFloors"],
    }),
    getPriceQuoteFloor: build.query({
      query: (floorId) => `/quoteFloors/${floorId}`,
      providesTags: ["QuoteFloor"],
    }),
    updatePriceQuoteFloor: build.mutation({
      query: ({ quoteId, floorId, ...body }) => ({ method: `PATCH`, url: `/quoteFloors/${floorId}`, body }),
      invalidatesTags: ["QuoteFloors", "QuoteFloor"],
      onQueryStarted: ({ quoteId, floorId, ...body }, { dispatch }) => {
        dispatch(
          api.util.updateQueryData("getPriceQuoteFloors", quoteId, (floors = []) => {
            const index = floors.findIndex((floor) => floor.floorId === floorId);
            if (index !== -1) {
              Object.assign(floors[index], body);
            }
          }),
        );
        dispatch(
          api.util.updateQueryData("getPriceQuoteFloor", floorId, (floor = {}) => {
            Object.assign(floor, body);
          }),
        );
      },
    }),
    addPriceQuoteFloor: build.mutation({
      query: (body) => ({ method: `POST`, url: `/quoteFloors`, body }),
      invalidatesTags: ["QuoteFloors"],
    }),
    deletePriceQuoteFloor: build.mutation({
      query: (floorId) => ({ method: `DELETE`, url: `/quoteFloors/${floorId}` }),
      invalidatesTags: ["QuoteFloors"],
    }),
    duplicatePriceQuoteFloor: build.mutation({
      query: (body) => ({ method: `POST`, url: `/fc-price-quote/create/duplicate/floor`, body }),
      invalidatesTags: ["QuoteFloors"],
    }),
    getFloorProducts: build.query({
      query: (floorId) => `/quoteFloors/${floorId}/products`,
      transformResponse: (res) => res?._embedded?.quoteProducts ?? [],
      providesTags: ["QuoteFloorProducts"],
    }),
    deleteFloorProducts: build.mutation({
      query: ({ productId }) => ({ method: `DELETE`, url: `/quoteProducts/${productId}` }),
      invalidatesTags: ["QuoteFloorProducts", "QuoteFloors", "QuoteFloor", "PrcPrices"],
      onQueryStarted: ({ productId, floorId }, { dispatch }) => {
        dispatch(
          api.util.updateQueryData("getFloorProducts", floorId, (products = []) => {
            const index = products.findIndex((p) => p.productId === productId);
            if (index !== -1) products.splice(index, 1);
          }),
        );
      },
    }),
    updateFloorProducts: build.mutation({
      query: ({ productId, ...body }) => ({ method: `PATCH`, url: `/quoteProducts/${productId}`, body }),
      invalidatesTags: ["QuoteFloorProducts", "QuoteFloors", "QuoteFloor", "PrcPrices"],
      onQueryStarted: ({ productId, floorId, ...body }, { dispatch }) => {
        dispatch(
          api.util.updateQueryData("getFloorProducts", floorId, (products = []) => {
            const index = products.findIndex((product) => product.productId === productId);
            if (index !== -1) {
              Object.assign(products[index], body);
            }
          }),
        );
      },
    }),
    addFloorProducts: build.mutation({
      query: ({ productId, ...body }) => ({ method: `POST`, url: `/quoteProducts`, body }),
      invalidatesTags: ["QuoteFloorProducts", "QuoteFloors", "QuoteFloor", "PrcPrices"],
    }),
    getPdfOptions: build.query({ query: (id) => `/fcPdfOptions/${id}`, providesTags: ["PdfOptions"] }),
    updatePdfOptions: build.mutation({
      query: ({ quoteId, ...body }) => ({ method: `PATCH`, url: `/fcPdfOptions/${quoteId}`, body }),
      invalidatesTags: ["PdfOptions"],
    }),
    getCoverLetter: build.query({ query: (id) => `/fcCoverLetters/${id}`, providesTags: ["CoverLetter"] }),
    updateCoverLetter: build.mutation({
      query: ({ quoteId, ...body }) => ({ method: `PATCH`, url: `/fcCoverLetters/${quoteId}`, body }),
      invalidatesTags: ["CoverLetter"],
    }),
    getFloorLaborCodes: build.query({
      query: (floorId) => `/quoteFloors/${floorId}/labors`,
      transformResponse: (res) => res?._embedded?.quoteLaborItems ?? [],
      providesTags: ["QuoteFloorPlanLaborCodes"],
    }),
    updateFloorLaborCode: build.mutation({
      query: ({ laborCodeId, ...body }) => ({ method: `PATCH`, url: `/quoteLaborItems/${laborCodeId}`, body }),
      invalidatesTags: ["QuoteFloorPlanLaborCodes", "QuoteFloors", "QuoteFloor"],
      onQueryStarted: ({ laborCodeId, floorId, ...body }, { dispatch }) => {
        dispatch(
          api.util.updateQueryData("getFloorLaborCodes", floorId, (laborCodes = []) => {
            const index = laborCodes.findIndex((lc) => lc.laborCodeId === laborCodeId);
            if (index !== -1) {
              Object.assign(laborCodes[index], body);
            }
          }),
        );
      },
    }),
    deleteFloorLaborCode: build.mutation({
      query: (laborCodeId) => ({ method: `DELETE`, url: `/quoteLaborItems/${laborCodeId}` }),
      invalidatesTags: ["QuoteFloorPlanLaborCodes", "QuoteFloors", "QuoteFloor"],
    }),
    addFloorLaborCode: build.mutation({
      query: (body) => ({ method: `POST`, url: `/quoteLaborItems`, body }),
      invalidatesTags: ["QuoteFloorPlanLaborCodes", "QuoteFloors", "QuoteFloor"],
    }),
    getLaborPrice: build.query({ query: (body) => ({ method: `POST`, url: `/fc-labor-table/laborPrice`, body }) }),
    searchQuotes: build.query({
      query: (params = {}) => {
        const entries = Object.entries(params).flatMap(([key, value]) =>
          Array.isArray(value) ? value.map((v) => [key, v]) : [[key, value]],
        );
        return `/quotes${isEmpty(entries) ? "" : "?" + new URLSearchParams(entries).toString()}`;
      },
      transformResponse: (res) => res?._embedded?.quotes ?? [],
      providesTags: ["Quote"],
    }),
    // has some logic for updating prices from external pricing system, can't be easily replaced by updatePriceQuote
    deprecatedEditQuote: build.mutation({
      query: (body) => ({ method: `POST`, url: `/fc-price-quote/update/quote`, body }),
      onQueryStarted: async (body, { dispatch, queryFulfilled }) => {
        dispatch(showSpinner());
        await queryFulfilled;
        dispatch(hideSpinner());
      },
      invalidatesTags: [
        "Quote",
        "QuoteFloors",
        "QuoteFloor",
        "QuoteFloorPlanLaborCodes",
        "QuoteFloorProducts",
        "PrcPrices",
      ],
    }),
    deprecatedUpdateQuotePrices: build.mutation({
      query: (body) => ({ method: `POST`, url: `/fc-price-quote/update/prices/quote`, body }),
      invalidatesTags: [
        "Quote",
        "QuoteFloors",
        "QuoteFloor",
        "QuoteFloorPlanLaborCodes",
        "QuoteFloorProducts",
        "PrcPrices",
      ],
    }),
    getLaborCodes: build.query({
      query: () => `/laborCodes?size=300`,
      transformResponse: (res) => res?._embedded?.laborCodes ?? [],
    }),
    getAccounts: build.query({
      query: (params = {}) => {
        const searchParams = new URLSearchParams();
        Object.entries(params).forEach(([key, value]) => {
          if (value == null || value === "") return;
          if (Array.isArray(value)) {
            value.forEach((item) => searchParams.append(key, item));
          } else {
            searchParams.append(key, value);
          }
        });
        const queryString = searchParams.toString();
        return `/accounts${queryString ? `?${queryString}` : ""}`;
      },
      transformResponse: (res) => res?._embedded?.accounts ?? null,
    }),
    getProducts: build.query({
      query: (params) => `/fcProducts${isEmpty(params) ? "" : "?" + new URLSearchParams(params).toString()}`,
      transformResponse: (res) => res?._embedded?.fcProducts ?? [],
    }),
    getProductPrice: build.query({
      query: (body) => ({ method: `POST`, url: `/fc-price-quote/get/price`, body }),
    }),
    getPrcPrices: build.query({
      query: (floorId) => `/fc-price-quote/floors/${floorId}/PrcPrices`,
      providesTags: ["PrcPrices"],
    }),
    getFavoriteProducts: build.query({
      query: (userId) => `/fcProducts/search/favorites?userId=${userId}`,
      transformResponse: (res) => res?._embedded?.fcProducts ?? [],
      providesTags: ["FavoriteProducts"],
    }),
    getStoreLaborPrices: build.query({
      query: (params) => `/storeLaborPrices${isEmpty(params) ? "" : "?" + new URLSearchParams(params).toString()}`,
      transformResponse: (res) => res?._embedded?.fcStoreLaborCodes ?? [],
      providesTags: ["StoreLaborPrices"],
    }),
    deprecatedUpdateLaborPricing: build.mutation({
      query: (body) => ({ method: `POST`, url: `/fc-labor-table/laborCode/update`, body }),
      invalidatesTags: ["StoreLaborPrices"],
    }),
    deprecatedCreateLaborPricing: build.mutation({
      query: (body) => ({ method: `POST`, url: `/fc-labor-table/laborCode`, body }),
      invalidatesTags: ["StoreLaborPrices"],
    }),
    deprecatedUpdateFavoriteProducts: build.mutation({
      query: (body) => ({ method: `POST`, url: `/fc-site-repo/fav/product`, body }),
      invalidatesTags: ["FavoriteProducts"],
    }),
    deprecatedUpdatePRC: build.mutation({
      query: (body) => ({ method: `POST`, url: `/fc-price-quote/createTagPrcDtls`, body }),
      invalidatesTags: ["PrcPrices", "QuoteFloorProducts", "QuoteFloors", "QuoteFloor"],
    }),
    deprecatedCreatePRC: build.mutation({
      query: (body) => ({ method: `POST`, url: `/fc-price-quote/createPrcHdr`, body }),
      invalidatesTags: ["PrcPrices", "QuoteFloorProducts", "QuoteFloors", "QuoteFloor"],
    }),
    getPriceQuoteFloorImage: build.query({
      query: (pictureURL) => ({
        url: pictureURL,
        responseHandler: (response) => response.blob(),
      }),
      transformResponse: (blob) => URL.createObjectURL(blob),
      async onCacheEntryAdded(pictureURL, { cacheDataLoaded, cacheEntryRemoved }) {
        const { data: objectURL } = await cacheDataLoaded;
        await cacheEntryRemoved;
        URL.revokeObjectURL(objectURL);
      },
    }),
  }),
});
